import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useSelector } from 'react-redux';
import config from 'constants/apiConfig';
import { selectGroupGuid } from 'slices/commonSelectors';
import { useCnicFlag } from 'utils/featureFlags';
import {
  CNIC_HAWAII_STORM_LAYER_DESCRIPTION,
  CNIC_HAWAII_STORM_LAYER_NAME,
  CNIC_HAWAII_STORM_POLYGON_COLOR,
  CNIC_HAWAII_STORM_POLYGON_OPACITY,
  CNIC_HAWAII_STORM_TRACK,
  CNIC_HAWAII_STORM_TRACK_DESCRIPTION,
  CNIC_HAWAII_STORM_TRACK_NAME,
  CNIC_HAWAII_STORM_WARNING_DESCRIPTION,
  CNIC_HAWAII_STORM_WARNING_NAME,
  CNIC_HAWAII_STORM_WARNING_RING,
} from 'components/MapPage/CorporateViewTabs/NotificationsTab/cnicHawaiiStormLayerData';
import type { CreateIcsIncidentLocationBody } from 'hooks/icsLocation/icsIncidentLocation.types';
import { OIL_SPILL_RESOURCE } from 'hooks/icsLocation/icsIncidentLocation.types';
import { internalLayersKeys } from 'hooks/internalLayer/queryKeys';
import { layerNodesKeys } from 'hooks/layerNode/queryKeys';
import type {
  CreateInternalLayerBody,
  InternalLayer,
  ListInternalLayersFilter,
  ListLayersResponse,
} from 'hooks/internalLayer/types';
import { buildSearchQueryParams } from 'hooks/shared/buildSearchQueryParams';

const ensureLocks = new Map<string, Promise<unknown>>();

const STORM_LOCATIONS: CreateIcsIncidentLocationBody[] = [
  {
    name: CNIC_HAWAII_STORM_WARNING_NAME,
    description: CNIC_HAWAII_STORM_WARNING_DESCRIPTION,
    resourceType: 'customPolygon',
    geometry: {
      type: 'Polygon',
      coordinates: [CNIC_HAWAII_STORM_WARNING_RING],
    },
    properties: {
      polygonColor: CNIC_HAWAII_STORM_POLYGON_COLOR,
      polygonOpacity: CNIC_HAWAII_STORM_POLYGON_OPACITY,
    },
  },
  {
    name: CNIC_HAWAII_STORM_TRACK_NAME,
    description: CNIC_HAWAII_STORM_TRACK_DESCRIPTION,
    // LineStrings only draw for oilSpill / boom in syncInternalLayers.
    resourceType: OIL_SPILL_RESOURCE,
    geometry: {
      type: 'LineString',
      coordinates: CNIC_HAWAII_STORM_TRACK,
    },
    properties: {},
  },
];

async function findStormLayer(
  groupGuid: string
): Promise<InternalLayer | undefined> {
  const resp = await axios.get<ListLayersResponse<InternalLayer>>(
    config.apiGateway.internal_layers,
    {
      params: buildSearchQueryParams<ListInternalLayersFilter>({
        filter: { groupGuid, search: CNIC_HAWAII_STORM_LAYER_NAME },
        perPage: 50,
      }),
    }
  );
  return (resp.data?.data ?? []).find(
    (layer) => layer.name === CNIC_HAWAII_STORM_LAYER_NAME
  );
}

async function ensureCnicHawaiiStormLayerForGroup({
  groupGuid,
  queryClient,
}: {
  groupGuid: string;
  queryClient: ReturnType<typeof useQueryClient>;
}): Promise<boolean> {
  const existing = await findStormLayer(groupGuid);
  if (existing) {
    if (existing.defaultVisible === true) return false;
    await axios.patch(`${config.apiGateway.internal_layers}/${existing.id}`, {
      defaultVisible: true,
    });
    await queryClient.invalidateQueries({
      queryKey: internalLayersKeys.all(),
    });
    return true;
  }

  const body: CreateInternalLayerBody = {
    groupGuid,
    incidentId: null,
    name: CNIC_HAWAII_STORM_LAYER_NAME,
    description: CNIC_HAWAII_STORM_LAYER_DESCRIPTION,
    defaultVisible: true,
    icsIncidentLocations: STORM_LOCATIONS,
  };
  await axios.post<InternalLayer>(config.apiGateway.internal_layers, body);
  await queryClient.invalidateQueries({ queryKey: internalLayersKeys.all() });
  await queryClient.invalidateQueries({ queryKey: layerNodesKeys.all() });
  return true;
}

/**
 * Seeds a hub-scoped internal layer with the synthetic Hawaii tropical-storm
 * warning polygon and approach track when CNIC is on.
 */
export function useEnsureCnicHawaiiStormLayer() {
  const cnicEnabled = useCnicFlag();
  const groupGuid = useSelector(selectGroupGuid);
  const queryClient = useQueryClient();
  const completedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!cnicEnabled || !groupGuid) {
      return;
    }
    if (completedRef.current === groupGuid) {
      return;
    }

    const lockKey = `cnic-storm-layer:${groupGuid}`;
    let cancelled = false;

    const run = async () => {
      const existing = ensureLocks.get(lockKey);
      if (existing) {
        await existing;
        if (!cancelled) completedRef.current = groupGuid;
        return;
      }

      const promise = ensureCnicHawaiiStormLayerForGroup({
        groupGuid,
        queryClient,
      }).finally(() => {
        ensureLocks.delete(lockKey);
      });
      ensureLocks.set(lockKey, promise);

      try {
        await promise;
        if (!cancelled) completedRef.current = groupGuid;
      } catch (error) {
        console.error('Failed to ensure CNIC Hawaii storm layer', error);
      }
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [cnicEnabled, groupGuid, queryClient]);
}
